import { inject, Injectable, signal } from '@angular/core';
import { OrganizationSettingsService } from './organization-settings.service';
import { UtilsService } from 'src/app/common/services/utils.service';
import { environment } from 'src/environments/environment';
import { RolesPolicyResponse } from '../models/roles-policy.model';
import { PermissionMatrixUpdateRequest, PermissionMatrixUpdateResponse } from '../models/permission-matrix-update.model';
import { CoreService } from 'src/app/common';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class OrganizationRolesService {

  private _rolesPolicy = signal<RolesPolicyResponse | null>(null);
  public rolesPolicy = this._rolesPolicy.asReadonly();
  private _rolesPolicyOrgId = signal<number | null>(null);
  public rolesPolicyOrgId = this._rolesPolicyOrgId.asReadonly();

  private identityBaseUrl = environment.identityUrl;
  private core = inject(CoreService<{}>);

  constructor(
    private utils: UtilsService
  ) {}

    async loadRolesPolicyIfNeeded(orgId: number, force = false): Promise<void> {
        const currentPolicyOrgId = this._rolesPolicyOrgId();
        const hasPolicyForOrg = this._rolesPolicy() && currentPolicyOrgId === orgId;
        if (hasPolicyForOrg && !force) return;

        try {
            const response = await this.getRolesPolicy(orgId);
            this.setRolesPolicyCache(orgId, response);
        } catch (error) {
            this.utils.logger.error('Failed to load roles policy', error);
            this.setRolesPolicyCache(null, null);
        }
    }

    /**
     * Fetches the permission matrix for the specified organization.
     * API: GET /api/orgs/{orgId}
     */
    async getRolesPolicy(orgId: number): Promise<RolesPolicyResponse | null> {
        this.utils.logger.info('getRolesPolicy() Fetching roles policy for org:', orgId);

        if (orgId == null) {
            this.utils.logger.warn('getRolesPolicy() No orgId provided');
            return null;
        }

        try {
            const url = `${this.identityBaseUrl}/PermissionMatrix/${orgId}`;
            this.utils.logger.info('getRolesPolicy() API URL:', url);

            const data = await firstValueFrom(this.core.get(url));
            return data as RolesPolicyResponse;
        } catch (error) {
            this.utils.logger.error('getRolesPolicy() API error details:', error);
            return null;
        } finally {
            this.utils.logger.info('getRolesPolicy() Completed fetching roles policy for org:', orgId);
        }
    }

    /**
     * Saves permission matrix overrides for the organization.
     * API: PUT /api/v1/PermissionMatrix/{orgId}
     */
    async savePermissionMatrix(orgId: number, payload: PermissionMatrixUpdateRequest): Promise<PermissionMatrixUpdateResponse | null> {
        this.utils.logger.info('savePermissionMatrix() Saving permission matrix for org:', orgId);

        if (orgId == null) {
            this.utils.logger.warn('savePermissionMatrix() No orgId provided');
            return null;
        }

        try {
            const url = `${this.identityBaseUrl}/PermissionMatrix/${orgId}`;
            this.utils.logger.info('savePermissionMatrix() API URL:', url);

            const data = await firstValueFrom(this.core.bulkPut(url, payload));
            const response = data as PermissionMatrixUpdateResponse;
            const updatedMatrix = response?.matrix ?? (response as any)?.Matrix;

            if (updatedMatrix) {
                const normalizedMatrix: RolesPolicyResponse = {
                    ...updatedMatrix,
                    permissionMatrixVersion: updatedMatrix.permissionMatrixVersion
                        ?? response?.permissionMatrixVersion
                        ?? (response as any)?.PermissionMatrixVersion
                };
                this.setRolesPolicyCache(orgId, normalizedMatrix);
            }

            return response;
        } catch (error) {
            this.utils.logger.error('savePermissionMatrix() API error details:', error);
            throw error;
        } finally {
            this.utils.logger.info('savePermissionMatrix() Completed saving permission matrix for org:', orgId);
        }
    }

    /**
     * Resets permission matrix overrides to defaults for the organization.
     * API: POST /api/v1/PermissionMatrix/{orgId}/reset
     */
    async resetPermissionMatrix(orgId: number): Promise<RolesPolicyResponse | null> {
        this.utils.logger.info('resetPermissionMatrix() Resetting permission matrix for org:', orgId);

        if (orgId == null) {
            this.utils.logger.warn('resetPermissionMatrix() No orgId provided');
            return null;
        }

        try {
            const url = `${this.identityBaseUrl}/PermissionMatrix/${orgId}/reset`;
            this.utils.logger.info('resetPermissionMatrix() API URL:', url);

            const data = await firstValueFrom(this.core.post(url, {}));
            const response = data as RolesPolicyResponse;
            this.setRolesPolicyCache(orgId, response);
            return response;
        } catch (error) {
            this.utils.logger.error('resetPermissionMatrix() API error details:', error);
            throw error;
        } finally {
            this.utils.logger.info('resetPermissionMatrix() Completed reset for org:', orgId);
        }
    }

    public setRolesPolicyCache(orgId: number | null, response: RolesPolicyResponse | null): void {
        this._rolesPolicy.set(response);
        this._rolesPolicyOrgId.set(response && orgId ? orgId : null);
    }

    public clearCache(): void {
        this.setRolesPolicyCache(null, null);
    }
}
